import { action, observable } from 'mobx';
import { PodiumAccessType } from '../../../../../../api/podium.dao';
import { ConnectionTemplatesFormVM } from '../../connection-template-form.viewmodel';
import { FieldKey } from '../../connection-template-form.config';
import {
    DEFAULT_PARTICIPANTS_LABEL,
    PARTICIPANTS_SECTION_LABEL_REQUIRED_ERROR
} from '../../../../../_events/_manage-event/_connections/_meeting-connections-form/podium-form/podium-form.texts';
import { plainText } from '../../../../../../modules/validators';

class TemplatePodiumFormVM {
    @observable
    public podiumLinkEnabled = false;
    @observable
    public podiumPresenterViewEnabled = false;
    @observable
    public podiumAccessType = PodiumAccessType.oeCentralSignIn;
    @observable
    public podiumParticipantsEnabled = false;
    @observable
    public podiumAttendeesEnabled = false;
    @observable
    public podiumQuestionAnswerEnabled = false;
    @observable
    public podiumChatEnabled = false;
    @observable
    public podiumParticipantsLabel = DEFAULT_PARTICIPANTS_LABEL;
    @observable
    public participantsLabelTouched = false;

    constructor(
        private readonly connectionTemplatesFormVM,
    ) {

    }

    public get formVM(): ConnectionTemplatesFormVM {
        return this.connectionTemplatesFormVM;
    }

    @action
    public initFormData = (): void => {
        const {
            podiumLinkEnabled,
            podiumPresenterViewEnabled,
            podiumAccessType,
            podiumParticipantsEnabled,
            podiumQuestionAnswerEnabled,
            podiumAttendeesEnabled,
            podiumChatEnabled,
            podiumParticipantsLabel,
        } = this.formVM.getFormFields(FieldKey);
        this.podiumLinkEnabled = podiumLinkEnabled.value as unknown as boolean;
        this.podiumPresenterViewEnabled = podiumPresenterViewEnabled.value as unknown as boolean;
        this.podiumAccessType = podiumAccessType.value as unknown as PodiumAccessType;
        this.podiumParticipantsEnabled = podiumParticipantsEnabled.value as unknown as boolean;
        this.podiumAttendeesEnabled = podiumAttendeesEnabled.value as unknown as boolean;
        this.podiumQuestionAnswerEnabled = podiumQuestionAnswerEnabled.value as unknown as boolean;
        this.podiumChatEnabled = podiumChatEnabled.value as unknown as boolean;
        this.podiumParticipantsLabel = (podiumParticipantsLabel.value as unknown as string) || DEFAULT_PARTICIPANTS_LABEL;
        this.participantsLabelTouched = false;
    };

    @action
    public togglePodiumEnabled = (): void => {
        this.podiumLinkEnabled = !this.podiumLinkEnabled;
    };

    @action
    public togglePresenterViewEnabled = (): void => {
        this.podiumPresenterViewEnabled = !this.podiumPresenterViewEnabled;
    };

    @action
    public setAccessType = (value: PodiumAccessType): void => {
        this.podiumAccessType = value;
    };

    @action
    public checkQuestionAnswerFeature = (): void => {
        this.podiumQuestionAnswerEnabled = !this.podiumQuestionAnswerEnabled;
    };

    @action
    public checkParticipantsFeature = (): void => {
        this.podiumParticipantsEnabled = !this.podiumParticipantsEnabled;
        if (!this.podiumParticipantsEnabled) {
            this.podiumParticipantsLabel = DEFAULT_PARTICIPANTS_LABEL;
        }
    };

    @action
    public setParticipantsLabel = (label: string): void => {
        this.podiumParticipantsLabel = label.trimLeft();
    };

    @action
    public setParticipantsLabelTouched = (): void => {
        this.participantsLabelTouched = true;
    };

    public get participantsLabelErrorMessage(): string {
        if (!this.podiumParticipantsLabel.trim()) {
            return this.formVM.getIntl.formatMessage(PARTICIPANTS_SECTION_LABEL_REQUIRED_ERROR);
        }
        const maxLengthResult = plainText.maxLength.short(this.podiumParticipantsLabel);
        if (!maxLengthResult.valid) {
            return this.formVM.getIntl.formatMessage(maxLengthResult.validationMessage);
        }
        return '';
    }

    @action
    public checkAttendeesFeature = (): void => {
        this.podiumAttendeesEnabled = !this.podiumAttendeesEnabled;
    };

    @action
    public checkChatFeature = (): void => {
        this.podiumChatEnabled = !this.podiumChatEnabled;
    };

    @action
    public updatePodiumDetails = (): void => {
        const {
            podiumLinkEnabled,
            podiumPresenterViewEnabled,
            podiumAccessType,
            podiumParticipantsEnabled,
            podiumQuestionAnswerEnabled,
            podiumAttendeesEnabled,
            podiumChatEnabled,
            podiumParticipantsLabel,
        } = this.formVM.getFormFields(FieldKey);
        if (this.podiumParticipantsEnabled && this.participantsLabelErrorMessage) {
            this.participantsLabelTouched = true;
            return;
        }
        podiumParticipantsLabel.setValue(this.podiumParticipantsLabel.trim());
        podiumLinkEnabled.setValue(this.podiumLinkEnabled);
        podiumPresenterViewEnabled.setValue(this.podiumPresenterViewEnabled);
        podiumAccessType.setValue(this.podiumAccessType);
        podiumParticipantsEnabled.setValue(this.podiumParticipantsEnabled);
        podiumAttendeesEnabled.setValue(this.podiumAttendeesEnabled);
        podiumQuestionAnswerEnabled.setValue(this.podiumQuestionAnswerEnabled);
        podiumChatEnabled.setValue(this.podiumChatEnabled);
        this.formVM.closePodiumPopup();
    };
}

export default TemplatePodiumFormVM;
